import { debounce } from '@/utils/debounce';
import { getByPath } from '@/utils/objectPath';
import type { App } from 'vue';

import { Availability } from '@freelancerepublik/models-core/MarketSituation.core';
import { clone } from '@freelancerepublik/helpers';

import { BorylPlanId, borylPlans } from './borylTrackingPlan';
import legacyEvents from './legacyEvents';

import { ENV } from '../../../env';

type FirstUseDataStore = { [key: string]: string | string[] | null | Availability };

declare global {
  interface Window {
    dataLayer: Record<string, any>;
  }
}

type LegacyDatalayerPayload = {
  event?: string;
} & Record<string, any>;

interface LegacyGtmEvent {
  action: string;
  category: string;
  label: string;
}

window.dataLayer = window.dataLayer || [];

const defaultCfg = {
  id: undefined as string | undefined,
  enabled: false,
  debug: false,
};
let config = defaultCfg;
const { dataLayer } = window;
const gtmVars = {
  user: {} as Record<string, any>,
  freelance: {} as Record<string, any>,
  company: {} as Record<string, any>,
};

let synced = false;

const log = ({ funcName = '', msg = '' }) => {
  if (config.debug) {
    console.log(`[gtm:${funcName}()] ${msg}`);
  }
};

const loadScript = (id: string) => {
  const script = document.createElement('script');
  dataLayer.push({
    event: 'gtm.js',
    'gtm.start': new Date().getTime(),
  });
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  document.body.appendChild(script);
};

const sendVars = debounce(() => {
  dataLayer.push({
    event: 'varsContent',
    vars: gtmVars,
  });
}, 250);

const gtm = {
  isSynced() {
    log({
      funcName: 'isSynced',
      msg: `event: varsContent, vars: ${JSON.stringify(gtmVars)}, synced: ${synced}`,
    });
    sendVars();

    if (!synced) {
      synced = true;
      dataLayer.push({ event: 'dataLayerReady' });
    }
  },
  trackPageView(url: string, title: string, hasData: boolean) {
    console.assert(typeof hasData === 'boolean', 'Invalid param type hasData - Must be a boolean');
    if (url.startsWith('/loading')) return;
    log({ funcName: 'trackPageView', msg: `url: ${url}, title: ${title}, hasData: ${hasData}` });
    dataLayer.push({
      event: 'virtualPageView',
      virtualPageURL: url,
      virtualPageTitle: title,
      hasData,
    });
  },
  trackUserVar(name: string, value: string) {
    log({ funcName: 'trackUserVar', msg: `name: ${name}, value: ${value}` });
    gtmVars.user[name] = value;
    sendVars();
  },
  trackFreelanceVar(name: string, value: string) {
    log({ funcName: 'trackFreelanceVar', msg: `name: ${name}, value: ${value}` });
    gtmVars.freelance[name] = value;
    sendVars();
  },
  trackCompanyVar(name: string, value: string) {
    log({ funcName: 'trackCompanyVar', msg: `name: ${name}, value: ${value}` });
    gtmVars.company[name] = value;
    sendVars();
  },
  trackLegacyEvent(arg: string | LegacyGtmEvent) {
    const event: LegacyGtmEvent = typeof arg === 'string' ? legacyEvents[arg] : arg;

    if (!event) {
      throw new Error(`Invalid GTM event ${arg} is incorrect`);
    }

    const data: LegacyDatalayerPayload = {
      event: 'eventGA',
      eventCategory: event.category,
      eventAction: event.action,
    };

    if (event.label) {
      data.eventLabel = event.label;
    }
    log({ funcName: 'trackLegacyEvent', msg: `data: ${JSON.stringify(data)}` });
    dataLayer.push(data);
  },
  legacyEvents,
  trackBorylPlan(planId: BorylPlanId, sequence: number, firstUseDataStore?: FirstUseDataStore) {
    const plan = borylPlans[planId];

    if (sequence > plan.length) {
      throw new Error('Attempt to track an event out of the Boryl plan !');
    }

    const event = clone(plan[sequence]);

    if (ENV.NODE_ENV === 'development') {
      console.log(event);
    }

    if (firstUseDataStore) {
      Object.entries(event)
        .filter(([eventKey]) => !eventKey.startsWith('event'))
        .forEach(([eventDataKey, eventDataValue]) => {
          if (typeof eventDataValue === 'string' || Array.isArray(eventDataValue)) {
            event[eventDataKey] = getByPath(firstUseDataStore, eventDataValue);
          } else if (eventDataValue === null) {
            event[eventDataKey] = eventDataValue;
          } else {
            const { path, transform } = eventDataValue;
            const firstUseValue = getByPath(firstUseDataStore, path);
            const transformedValue = transform(firstUseValue);
            event[eventDataKey] = transformedValue;
          }
        });
    }

    dataLayer.push(event);
  },
};

export type GtmType = typeof gtm;

import { gtmKey } from '@/infra/injection-keys';
export { gtmKey };

const _instance = gtm;

export const gtmPlugin = {
  _instance,
  install(app: App, initCfg: Partial<typeof defaultCfg> = {}) {
    config = { ...defaultCfg, ...initCfg };

    app.provide(gtmKey, gtm);

    if (config.enabled && config.id) {
      loadScript(config.id);
    }
  },
};
